import * as IoC from 'typescript-ioc';
import React from 'react';
import { action, observable } from 'mobx';
import { ConnectionTemplateMapper, ConnectionTemplateValues } from './connection-template.mapper';
import { ConnectionType, IConnectionDetailsDTO, KnovioLabel, WebinarAutoRecording } from '../../../../api/connections.dao';
import { IntlShape } from 'react-intl/lib';
import { configList, FieldKey } from './connection-template-form.config';
import { BLUEPRINTS_TRACKING_EVENTS, convertBoolTypeEmptyForMixPanel, isTrackingFieldEnabled } from '../../../../tracking-service/tracking-values';
import { TObject } from '../../../../infrastructure/type-extensions';
import {
    IAudioBridgeConnection,
    IConnectionTemplateConnectionGroupDTO,
    IConnectionTemplateDTO,
    ICreateConnectionTemplateDTO,
    IKnovioConnection,
    IOtherConnection,
    IRoleConnectionsValue,
    RoleConnectionKey,
    ITemplateConnectioinGroupConnections,
    IZoomConnection,
    IZoomWebinarConnection,
    TConnection,
    RoleConnectionTargetType
} from '../../../../api/blueprints/connection.template.types';
import { BaseBlueprintFormVM } from '../../blueprint-form-base.viewmodel';
import { ConnectionKnovioForm } from './components/conections-list/knovio-connection/connection-knovio-form';
import { ConnectionOtherForm } from './components/conections-list/other-connection/connection-other-form';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { FormattedMessage } from 'react-intl';
import { Space } from '../../../../constants/html-character-entities';
import { IConnectionTypeInternal } from '../../../_events/_manage-event/shared/connections-list/connections-list.viewmodel';
import { autoRegEnabledSetting, autoSyncEnabledSetting, passportReplayEnabledSetting } from '../../../../constants/blueprint-labels.constants';
import { connectionsToBluepringTemplateDTO } from '../../../../infrastructure/utils/connections.utils';
import {
    BLUEPRINTS_CONNECTION_FORM_CREATE_TITLE_TEXT,
    BLUEPRINTS_CONNECTION_FORM_EDIT_TITLE_TEXT,
    DELETE_CONNECTION_TEMPLATE_TEXT,
    FORM_ARE_YOU_SURE_TO_TEXT,
    THIS_CANNOT_BE_UNDONE_TEXT,
    YES_DELETE_TEXT
} from './connection-template-form.texts';
import { ConnectionZoomWebinarTemplateForm } from './components/conections-list/zoom-connection/zoom-webinar-connection/connection-zoom-webinar.form';
import { ConnectionZoomMeetingTemplateForm } from './components/conections-list/zoom-connection/zoom-meeting-connection/connection-zoom-meeting.form';
import { ConnectionAudioBridgeForm } from './components/conections-list/audio-bridge-connection/connection-audio-bridge-form';
import { ConnectionVersion } from '../../../../constants/common.constants';
import { autoSyncConnectionsLimitV1, autoSyncConnectionsLimitV2 } from '../../../../modules/connections/const';
import { EventName, IEventDispatcher } from '../../../../infrastructure/event-dispatcher';
import { v4 as uuid } from 'uuid';
import { ISubWindowFacade, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { CONFIGURE_PASSPORT_DISPLAY_TEXT } from '../../../../constants/common-labels.constants';
import { ConfigurePassportDisplayForm } from '../../../_events/_manage-event/shared/configure-passport-display/configure-passport-display.container';
import { IConfigurePassportDisplayFormProps } from '../../../_events/_manage-event/shared/configure-passport-display/configure-passport-display.viewmodel';
import { PassportConfigurationMode } from '../../../_events/_manage-event/shared/configure-passport-display/configure-passport-display.config';
import { getPodiumAccessType, PODIUM_ATTENDEES_LABEL, PODIUM_CHAT_LABEL, PODIUM_PARTICIPANTS_LABEL, PODIUM_QUESTION_ANSWER_LABEL } from '../../../_events/_manage-event/_connections/_meeting-connections-form/podium-form/podium-form.texts';
import { PodiumAccessType } from '../../../../api/podium.dao';
import { MANAGE_PODIUM_ACCESS_TEXT } from '../../../_events/_manage-event/_connections/_meeting-connections-form/meeting-connections-form.texts';
import { TemplatePodiumForm } from './connection-template-podium/template-podium-form/template-podium-form';
import { FormVmItemConfigType, InitialData } from '../../../../infrastructure/form-viewmodel-controller';
import {
    buildRoleConnectionOptions,
    connectionReferenceId,
    decodeRoleConnectionOptionId,
    emptyRoleConnections,
    encodeRoleConnectionOptionId,
    IRoleConnectionOption,
    normalizeRoleConnections,
    pruneRoleConnections,
    resolveRoleConnectionLabel,
} from './connection-template-role-connections/role-connections.utils';

export interface IDetailsItemField {
    label: string;
    value: string;
    withCopyBtn?: boolean;
    showFullValue?: boolean;
};

const defaultTrueConfigValues = [
    FieldKey.PODIUM_PARTICIPANTS_ENABLED,
    FieldKey.PODIUM_QUESTION_ANSWER_ENABLED,
    FieldKey.PODIUM_CHAT_ENABLED,
];
export class ConnectionTemplatesFormVM extends BaseBlueprintFormVM {
    public static availableConnections: ConnectionType[] = [
        ConnectionType.Zoom,
        ConnectionType.Webinar,
        ConnectionType.Knovio,
        ConnectionType.AudioBridge,
        ConnectionType.Other,
    ];

    @observable
    private connectionsArray: TConnection[] = [];
    @observable
    private connectionGroupsArray: IConnectionTemplateConnectionGroupDTO[] = [];
    @observable
    private roleConnectionsState: IRoleConnectionsValue = emptyRoleConnections();
    @observable
    private configurePassportDisplayFormSubWindow: { [key: string]: ISubWindowFacade };
    @observable
    private podiumPopupMap: ISubWindowFacade;
    @observable
    private updatedConfig = configList;
    constructor(
        private readonly accountId: number,
        protected readonly blueprintId: string,
        private readonly intl: IntlShape,
        private readonly apiCallSuccessCallback: () => void,
        private readonly deleteCompletionCallback: () => void,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly eventDispatcher: IEventDispatcher = IoC.Container.get(IEventDispatcher),
    ) {
        super(blueprintId);
        this.configurePassportDisplayFormSubWindow = {};
    }

    public get getIntl(): IntlShape {
        return this.intl;
    }

    public get connections(): TConnection[] {
        return this.connectionsArray;
    }

    public get connectionGroups(): IConnectionTemplateConnectionGroupDTO[] {
        return this.connectionGroupsArray;
    }

    public get roleConnections(): IRoleConnectionsValue {
        return this.roleConnectionsState;
    }

    public get availableConnectionsItems(): IConnectionTypeInternal[] {
        return ConnectionTemplatesFormVM.availableConnections.map(connection => {
            return {
                id: connection,
                name: connection === ConnectionType.Knovio ? KnovioLabel.OEStream : connection,
            };
        });
    };

    public get connectionVersion(): ConnectionVersion {
        return this.formDataCtrl.getValue(FieldKey.CONNECTION_VERSION) ? ConnectionVersion.V2 : ConnectionVersion.V1;
    }

    public get isAutoSyncLimitReached(): boolean {
        const autoSyncConnectionsCount = this.countAutoSyncConnections();

        return this.connectionVersion === ConnectionVersion.V2
            ? autoSyncConnectionsCount >= autoSyncConnectionsLimitV2
            : autoSyncConnectionsCount >= autoSyncConnectionsLimitV1;
    }

    @action.bound
    public async initFormVM(): Promise<void> {
        if (this.blueprintId) {
            const result = await this.getBluePrint(this.accountId) as IConnectionTemplateDTO;
            this.fromDTO(result);
            this.updateConfigDefaultTrueValues(configList, this.blueprintItemSnapshot);
        }

        this.initializeFormData(this.updatedConfig, this.blueprintItemSnapshot);
    }

    public onConnectionTypeSelection = (connectionType: ConnectionType, connection?: TConnection): void => {
        if (connectionType === ConnectionType.Zoom) {
            this.showZoomMeetingConnectionForm(connection);
        } else if (connectionType === ConnectionType.Webinar) {
            this.showZoomWebinarConnectionForm(connection);
        } else if (connectionType === ConnectionType.Knovio) {
            this.showKnovioConnectionForm(connection);
        } else if (connectionType === ConnectionType.Other) {
            this.showOtherConnectionForm(connection);
        } else if (connectionType === ConnectionType.AudioBridge) {
            this.showAudioBridgeConnectionForm(connection);
        }
    };

    public getConnectionSettingsValue = (connection: TConnection): React.ReactElement => {
        if ((connection as IZoomConnection).autoRegistrationEnabled) {
            return autoRegEnabledSetting;
        };

        if ((connection as IZoomConnection).autoSyncEnabled) {
            return autoSyncEnabledSetting;
        };

        if ((connection as IKnovioConnection).passportReplayEnabled) {
            return passportReplayEnabledSetting;
        };
    };

    @action.bound
    public async createConnectionTemplate(): Promise<boolean> {
        try {
            this.closePodiumPopup();
            this.startLoading();
            const dto = ConnectionTemplateMapper.toConnectionTemplateDTO(this.composeConnectionTemplateValues());
            await this.blueprintsDAO.createBlueprint<ICreateConnectionTemplateDTO, IConnectionTemplateDTO>(dto);
            this.trackSubmitBlueprintFormMixPanel();

            this.apiCallSuccessCallback();
            return true;
        } catch (error) {
            ConnectionTemplatesFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @action
    public editConnectionTemplate = async (): Promise<boolean> => {
        try {
            this.closePodiumPopup();
            this.startLoading();
            const additionalValues = this.composeAdditionalTemplateValues();
            const dto = ConnectionTemplateMapper.toEditConnectionTemplateDTO(this.composeConnectionTemplateValues(additionalValues));
            await this.blueprintsDAO.editBlueprint<IConnectionTemplateDTO>(dto);
            this.trackSubmitBlueprintFormMixPanel();

            this.apiCallSuccessCallback();
            return true;
        } catch (error) {
            ConnectionTemplatesFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    };

    public submitForm = (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            return this.callProperSubmitAction();
        }
    };

    @action
    public addConnection = (connection: TConnection): boolean => {
        this.validateUniqueReference(connection);
        if (!this.validateUniqueConnectionId(connection)) return false;
        this.connections.push(connection);
        this.eventDispatcher.dispatch(EventName.ConnectionCreated, connection);
        return true;
    };

    @action
    public editConnection = (connection: TConnection): boolean => {
        this.validateUniqueReference(connection);
        if (!this.validateUniqueConnectionId(connection)) return false;
        const selectedConnectionIdx = this.connections.findIndex(c => c.connectionId === connection.connectionId);
        const previousReferenceId = selectedConnectionIdx > -1
            ? connectionReferenceId(this.connectionsArray[selectedConnectionIdx])
            : null;
        const selectedConnection = selectedConnectionIdx > -1
            ? Object.assign(this.connectionsArray[selectedConnectionIdx], connection)
            : connection;
        const nextReferenceId = connectionReferenceId(connection);
        if (previousReferenceId && previousReferenceId !== nextReferenceId) {
            this.retargetRoleConnection('connection', previousReferenceId, nextReferenceId);
        }
        this.eventDispatcher.dispatch(EventName.ConnectionUpdated, selectedConnection);
        this.updateConnectionInGroupCallback(connection, this.connectionsArray[selectedConnectionIdx]);
        this.connectionsArray[selectedConnectionIdx] = selectedConnection;
        return true;
    };

    @action
    private updateConnectionInGroupCallback(connection: TConnection, selectedConnection: TConnection): void {
        this.connectionGroups.forEach(group => {
            const existingConnection = group.connections.find(c => this.findConnectionInGroup(c, selectedConnection));
            if (existingConnection) {
                // @ts-ignore
                existingConnection.connectionId = connection.customConnectionId || connection.connectionId;
                return true;
            }
        });
    };

    @action
    private removeConnectionCallback(connection: TConnection): void {
        this.connectionGroups.forEach(group => {
            const newConnection = group.connections.filter(c => !this.findConnectionInGroup(c, connection));
            group.connections = newConnection;
        });
    }

    @action
    public removeConnection = (connection: TConnection): void => {
        this.removeConnectionCallback(connection);
        this.clearRoleConnectionTarget('connection', connectionReferenceId(connection));
        this.clearRoleConnectionTarget('connection', connection.connectionId);
        this.connectionsArray = this.connections.filter(c => c.connectionId !== connection.connectionId);
        this.eventDispatcher.dispatch(EventName.ConnectionDeleted, { connectionId: connection.connectionId });
    };

    public openEditConnectionForm = (connection: TConnection) => {
        this.onConnectionTypeSelection(connection.type, connection);
    };

    @action
    public configurePassportDisplay = (connection: TConnection): void => {
        const popup = connection.connectionId;
        const existingSubWindow = this.configurePassportDisplayFormSubWindow?.[popup] || null;
        if (existingSubWindow && existingSubWindow?.getStatus() !== SubWindowStatus.Closed) {
            // if we successfully maximized existing one
            existingSubWindow.maximize();
        }

        if (!this.configurePassportDisplayFormSubWindow[popup] || this.configurePassportDisplayFormSubWindow[popup].getStatus() === SubWindowStatus.Closed) {
            this.configurePassportDisplayFormSubWindow[popup] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(CONFIGURE_PASSPORT_DISPLAY_TEXT),
                className: 'oe-popup-layout--positioned',
                componentToHost: ConfigurePassportDisplayForm,
                componentToHostProps: {
                    props: {
                        accountId: this.accountId,
                        eventId: null,
                        meetingConnectionId: connection.connectionId,
                        blueprintId: this.blueprintId,
                    } as unknown as IConfigurePassportDisplayFormProps,
                    connection: connection as IConnectionDetailsDTO,
                    passportConfigurationMode: PassportConfigurationMode.ConnectionTemplate,
                    blueprintPassportConfiguration: connection.passportConfiguration,
                    editBlueprintConnection: (item: TConnection): void => {
                        if (this.editConnection(item)) {
                            window.close();
                        }
                    },
                },
                onCloseIconClickHandler: () => {
                    this.configurePassportDisplayFormSubWindow[popup].close();
                    return true;
                }
            });
        }
    };

    @action
    public setDefaultConnection = (connection: TConnection | IConnectionTemplateConnectionGroupDTO): void => {
        this.connections.forEach(c => this.toggleDefaultConnection(c, c === connection));
        this.connectionGroups.forEach(c => this.toggleDefaultConnection(c, c === connection));
    };

    public composeZoomWebinarConnectionDetails = (connection: Extract<TConnection, IZoomWebinarConnection>, groupName: string): IDetailsItemField[] => {
        const labelField = { label: `${connection.type} Label`, value: connection.label };
        const groupField = { label: `${connection.type} Group`, value: groupName };
        const settingsValues = [
            connection.autoRecording === WebinarAutoRecording.Cloud && 'Automatically Record' || '',
            connection.autoRegistrationEnabled && 'Auto Reg' || '',
            connection.autoSyncEnabled && 'Auto Sync' || '',
        ];
        if (connection.panelistLinkCreationEnabled) {
            settingsValues.push('Panelist Link Creation Enabled');
        }
        const settingsField = { label: 'Settings', value: settingsValues.filter(Boolean).join(', '), showFullValue: true };

        return [labelField, groupField, settingsField];
    };

    public composeZoomMeetingConnectionDetails = (connection: Extract<TConnection, IZoomConnection>, groupName: string): IDetailsItemField[] => {
        const labelField = { label: `${connection.type} Label`, value: connection.label };
        const groupField = { label: `${connection.type} Group`, value: groupName };
        const settingsValues = [
            connection.autoRegistrationEnabled && 'Auto Reg' || '',
            connection.autoSyncEnabled && 'Auto Sync' || '',
        ];
        const settingsField = { label: 'Settings', value: settingsValues.filter(Boolean).join(', ') };

        return [labelField, groupField, settingsField];
    };

    public composeKnovioConnectionDetails = (connection: Extract<TConnection, IKnovioConnection>): IDetailsItemField[] => {
        const labelField = { label: `${KnovioLabel.OEStream} Label`, value: connection.title };
        const settingsField = { label: 'Settings', value: connection.passportReplayEnabled && 'Replay' || '' };

        return [labelField, settingsField];
    };

    public composeOtherConnectionDetails = (connection: Extract<TConnection, IOtherConnection>): IDetailsItemField[] => {
        const labelField = { label: 'Other Name', value: connection.name };
        const connectionlink = { label: 'Other Link', value: connection.connectionLink, withCopyBtn: true };


        return [labelField, connectionlink];
    };

    public composeAudiBeidgeConnectionDetails = (connection: Extract<TConnection, IAudioBridgeConnection>): IDetailsItemField[] => {
        const labelField = { label: 'Audio Bridge Label', value: connection.label };
        const settingsValues = [
            connection.recordCalls ? 'Automatically Record Enabled' : 'Automatically Record Disabled',
            connection.autoSyncEnabled && 'Auto Sync' || '',
        ];
        const settingsField = { label: 'Settings', value: settingsValues.filter(Boolean).join(', '), showFullValue: true };

        return [labelField, settingsField];
    };

    public getConnectionLabel = (connection: TConnection): string => {
        return connection.label || (connection as IOtherConnection).name || (connection as IKnovioConnection).title || '';
    };

    public trackSubmitBlueprintFormMixPanel = (): void => {
        const eventType = this.isEditMode ? BLUEPRINTS_TRACKING_EVENTS.EDIT_CONNECTION_TEMPLATE : BLUEPRINTS_TRACKING_EVENTS.NEW_CONNECTION_TEMPLATE;
        const isPodiumEnabled = !!this.formDataCtrl.getValue(FieldKey.PODIUM_LINK_ENABLED);

        this.trackingService.get().track(eventType, {
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getAccount()?.name,
            'Template Name': this.buildEditableFieldCheckMixPanel(FieldKey.NAME),
            'Template ID': this.blueprintId || null,
            'Template Description': convertBoolTypeEmptyForMixPanel(!!this.formDataCtrl.getValue(FieldKey.DESCRIPTION)),
            'Meeting Guidelines': convertBoolTypeEmptyForMixPanel(!!this.formDataCtrl.getValue(FieldKey.GUIDELINES)),
            'V2 Connections': isTrackingFieldEnabled(!!this.formDataCtrl.getValue(FieldKey.CONNECTION_VERSION)),
            'Connection Groups': this.connectionGroups.length,
            'Auto Registration': isTrackingFieldEnabled(!!this.formDataCtrl.getValue(FieldKey.AUTO_REGISTRATION_ENABLED)),
            'Auto Subscription': isTrackingFieldEnabled(!!this.formDataCtrl.getValue(FieldKey.AUTO_SUBSCRIPTION_ENABLED)),
            'Podium': isTrackingFieldEnabled(isPodiumEnabled),
            'Podium Presenter View': isTrackingFieldEnabled(
                isPodiumEnabled && !!this.formDataCtrl.getValue(FieldKey.PODIUM_PRESENTER_VIEW_ENABLED)
            ),
            'Podium Access Type': getPodiumAccessType(
                this.formDataCtrl.getValue(FieldKey.PODIUM_ACCESS_TYPE) as PodiumAccessType
            ),
            'Podium Features': this.buildPodiumFeaturesForMixpanel(),
            'Role-Presenter': this.getRoleConnectionLabel('presenter'),
            'Role-Host': this.getRoleConnectionLabel('host'),
            'Role-Support': this.getRoleConnectionLabel('support'),
            ...this.buildConnectionsPropertiesMixpanel()
        });
    };

    public deleteBluePrint = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage {...FORM_ARE_YOU_SURE_TO_TEXT} />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage {...DELETE_CONNECTION_TEMPLATE_TEXT} />
                    </span>
                    <Space />
                    <FormattedMessage {...THIS_CANNOT_BE_UNDONE_TEXT} />
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage {...YES_DELETE_TEXT} />,
                modifier: 'confirm-warn',
                handler: this.deleteBlueprintApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    public deleteBlueprintApiCall = async (): Promise<void> => {
        try {
            this.closePodiumPopup();
            await this.blueprintsDAO.deleteBlueprint({
                accountId: this.accountId,
                blueprintId: this.blueprintId
            });

            this.trackDeleteBluePrintMixPanel();
            this.deleteCompletionCallback();
        } catch (error) {
            ConnectionTemplatesFormVM.handleApiException(error);
        }
    };

    @action
    public toggleConnectionVersion(value: boolean): void {
        this.connectionsArray = [];
        this.connectionGroupsArray = [];
        this.roleConnectionsState = emptyRoleConnections();
        this.formDataCtrl.setValue(FieldKey.AUTO_REGISTRATION_ENABLED, false);
        this.formDataCtrl.setValue(FieldKey.CONNECTION_VERSION, !value);
    }

    @action
    public addConnectionGroup(group): void {
        if (!group.connectionGroupId) {
            group.connectionGroupId = uuid();
        }
        this.connectionGroupsArray.push(group);
    }

    @action
    public editConnectionGroup(group): void {
        const selectedConnectionIdx = this.connectionGroups.findIndex(g => g.meetingConnectionId === group.meetingConnectionId);
        const previousGroup = this.connectionGroupsArray[selectedConnectionIdx];
        this.connectionGroupsArray[selectedConnectionIdx] = {
            ...group,
            connectionGroupId: group.connectionGroupId || previousGroup?.connectionGroupId || uuid(),
        };
    }

    @action
    public deleteConnectionGroup(groupId: string): void {
        const group = this.connectionGroups.find(item => item.meetingConnectionId === groupId);
        if (group?.connectionGroupId) {
            this.clearRoleConnectionTarget('connectionGroup', group.connectionGroupId);
        }
        this.connectionGroupsArray = this.connectionGroups.filter(g => g.meetingConnectionId !== groupId);
    }

    public getRoleConnectionOptions = (chooseLabel: string): IRoleConnectionOption[] => {
        return buildRoleConnectionOptions(this.connections, this.connectionGroups, chooseLabel);
    };

    public getRoleConnectionOptionId = (role: RoleConnectionKey): string => {
        const assignment = pruneRoleConnections(this.roleConnectionsState, this.connections, this.connectionGroups)[role];
        return encodeRoleConnectionOptionId(assignment);
    };

    public getRoleConnectionLabel = (role: RoleConnectionKey): string => {
        const assignment = pruneRoleConnections(this.roleConnectionsState, this.connections, this.connectionGroups)[role];
        const connectionLabel = resolveRoleConnectionLabel(assignment, this.connections, this.connectionGroups);
        return connectionLabel !== '' ? connectionLabel : 'None';
    };

    @action
    public setRoleConnection = (role: RoleConnectionKey, optionId: string): void => {
        const assignment = decodeRoleConnectionOptionId(optionId);
        const isKnownTarget = !assignment || this.roleTargetIsOnTemplate(assignment);
        this.roleConnectionsState = {
            ...this.roleConnectionsState,
            [role]: isKnownTarget ? assignment : null,
        };
    };

    public findCurrentConnection(connection: TConnection, checkedConnectionId: string): boolean {
        return (connection.connectionId === checkedConnectionId)
            // @ts-ignore
            || (connection.customConnectionId === checkedConnectionId)
            ;
    }

    public findConnectionInGroup(groupConnection: ITemplateConnectioinGroupConnections, connection: TConnection): boolean {
        if (!connection) return false;
        return (groupConnection.connectionId === connection.connectionId)
            // @ts-ignore
            || (groupConnection.connectionId === connection.customConnectionId)
            ;
    };

    @action
    public openPodiumPopup = (): void => {
        const existingSubWindowForPodiumMeeting = this.podiumPopupMap;

        if (!existingSubWindowForPodiumMeeting || existingSubWindowForPodiumMeeting?.getStatus() === SubWindowStatus.Closed) {
            this.podiumPopupMap = this.subWindowService.showSubWindow({
                title: this.getIntl.formatMessage(MANAGE_PODIUM_ACCESS_TEXT),
                componentToHost: TemplatePodiumForm,
                componentToHostProps: {
                    vm: this
                },
            });
        }
    };

    @action
    public closePodiumPopup = (): void => {
        this.podiumPopupMap?.close();
    };

    private showConnectionForm = (component: (props) => JSX.Element, connection?: TConnection, additionalProps: Object = {}): void => {
        const formTitle = connection
            ? this.intl.formatMessage(BLUEPRINTS_CONNECTION_FORM_EDIT_TITLE_TEXT)
            : this.intl.formatMessage(BLUEPRINTS_CONNECTION_FORM_CREATE_TITLE_TEXT);

        const window = this.subWindowService.showSubWindow({
            title: formTitle,
            componentToHost: component,
            componentToHostProps: {
                connection: connection,
                addConnection: (item: TConnection): void => {
                    if (this.addConnection(item)) {
                        window.close();
                    }
                },
                editConnection: (item: TConnection): void => {
                    if (this.editConnection(item)) {
                        window.close();
                    }
                },
                ...additionalProps
            }
        });
    };

    private composeAdditionalTemplateValues = (): TObject<string> => {
        return {
            blueprintId: this.blueprintId,
            createdAt: this.blueprintItemSnapshot.createdAt as string,
        };
    };

    private fromDTO = (dto: IConnectionTemplateDTO): void => {
        this.blueprintItemSnapshot = ConnectionTemplateMapper.toConnectionTemplateValuesFromConnectionTemplateDTO(dto);
        this.connectionsArray = this.blueprintItemSnapshot.connections as TConnection[] || [];
        this.connectionGroupsArray = this.composeConnectionGroupsFromAPI(
            this.blueprintItemSnapshot.connectionGroups as IConnectionTemplateConnectionGroupDTO[]
        ) || [];
        this.roleConnectionsState = pruneRoleConnections(
            normalizeRoleConnections(this.blueprintItemSnapshot.roleConnections as IRoleConnectionsValue),
            this.connectionsArray,
            this.connectionGroupsArray
        );
    };

    private showZoomWebinarConnectionForm = (zoomConnection?: TConnection): void => {
        const additionalProps = {
            isAutoSyncLimitReached: this.isAutoSyncLimitReached,
            connectionVersion: this.connectionVersion
        };
        this.showConnectionForm(ConnectionZoomWebinarTemplateForm, zoomConnection, additionalProps);
    };

    private showZoomMeetingConnectionForm = (zoomConnection?: TConnection): void => {
        const additionalProps = {
            isAutoSyncLimitReached: this.isAutoSyncLimitReached,
            connectionVersion: this.connectionVersion
        };
        this.showConnectionForm(ConnectionZoomMeetingTemplateForm, zoomConnection, additionalProps);
    };

    private showKnovioConnectionForm = (knovioConnection?: TConnection): void => {
        this.showConnectionForm(ConnectionKnovioForm, knovioConnection);
    };

    private showOtherConnectionForm = (otherConnection?: TConnection): void => {
        this.showConnectionForm(ConnectionOtherForm, otherConnection);
    };

    private showAudioBridgeConnectionForm = (audioBridgeConnection?: TConnection): void => {
        const additionalProps = {
            connectionVersion: this.connectionVersion,
            isAutoSyncLimitReached: this.isAutoSyncLimitReached
        };
        this.showConnectionForm(ConnectionAudioBridgeForm, audioBridgeConnection, additionalProps);
    };

    @action
    private composeConnectionTemplateValues(additionalValues: TObject<string> = {}): ConnectionTemplateValues {
        const values = { accountId: this.accountId, ...additionalValues } as ConnectionTemplateValues;

        this.formDataCtrl.getVmState().forEach(({ name, value }) => {
            values[name] = value;
        });

        this.connectionsArray = connectionsToBluepringTemplateDTO(this.connections);

        values.connections = this.connections;
        // eslint-disable-next-line no-unused-vars
        values.connectionGroups = this.connectionGroups.map(({ meetingConnectionId, ...rest }) => ({
            ...rest,
            connectionGroupId: rest.connectionGroupId,
        }));
        this.roleConnectionsState = pruneRoleConnections(
            this.roleConnectionsState,
            this.connections,
            this.connectionGroups
        );
        values.roleConnections = this.roleConnectionsState;

        return values;
    }

    private callProperSubmitAction = (): Promise<boolean> => {
        return this.isEditMode
            ? this.editConnectionTemplate()
            : this.createConnectionTemplate();
    };

    private buildConnectionsPropertiesMixpanel = (): TObject<string> => {
        const connectionsProperties = {};
        const connectionsCount = this.connections.reduce((countMap, item) => {
            const type = item.type;
            countMap[type] = (countMap[type] || 0) + 1;

            return countMap;
        }, {});
        const defaultConnectionType = this.connections.find(x => x.isDefaultConnection)?.type;
        const passportReplayEnabled = this.connections.find((x: IKnovioConnection) => x.passportReplayEnabled)?.type;
        ConnectionTemplatesFormVM.availableConnections.map(type => {
            const value = [
                connectionsCount[type],
                defaultConnectionType === type && 'Default',
                passportReplayEnabled === type && 'Replay'
            ].filter(Boolean).join(', ');
            const key = `Connection - ${type}`;
            connectionsProperties[key] = value || '0';
        });

        return connectionsProperties;
    };

    private validateUniqueReference = (connection: TConnection): void => {
        if (connection.meetingUniqueReference && this.isMeetingUniqueReferenceAlreadyExist(connection)) {
            const meetingUniqueReferenceError = `Unique Reference ${connection.meetingUniqueReference} is already assigned to a connection in this template`;
            BaseBlueprintFormVM.handleInternalException(meetingUniqueReferenceError);
            connection.meetingUniqueReference = 0;
        };
    };

    private isNewConnection = (connection: TConnection): boolean => {
        return !this.connections.find(c => c.connectionId === connection.connectionId);
    };

    private validateUniqueConnectionId = (connection: TConnection): boolean => {
        // @ts-ignore
        const connectionId = connection.customConnectionId || connection.connectionId;
        const isMeetingWithIdExist = this.connections
            // @ts-ignore
            .some(c => (c.connectionId === connectionId) || (c.customConnectionId === connectionId))
            ;
        if (this.isNewConnection(connection) && isMeetingWithIdExist) {
            const meetingUniqueReferenceError = `Connection Id "${connectionId}" is already assigned to a connection in this template`;
            BaseBlueprintFormVM.handleInternalException(meetingUniqueReferenceError);
            return false;
        }
        return true;
    };

    private isMeetingUniqueReferenceAlreadyExist = (connection: TConnection): boolean => {
        const existingInConnections = this.connections
            .find((c: IZoomConnection | IZoomWebinarConnection | IOtherConnection) => c.meetingUniqueReference === connection.meetingUniqueReference);
        const notSameConnection = existingInConnections?.connectionId !== connection.connectionId;

        return !!existingInConnections && notSameConnection;
    };

    private countAutoSyncConnections(): number {
        return this.connections.filter((connection: IZoomConnection) => connection.autoSyncEnabled).length;
    }

    private trackDeleteBluePrintMixPanel = (): void => {
        this.trackingService.get().track(BLUEPRINTS_TRACKING_EVENTS.DELETE_CONNECTION_TEMPLATE, {
            'Account Name': this.globalStorage.getAccount()?.name,
            'Account ID': this.accountId,
            'Connection Template Name': this.formDataCtrl.getValue(FieldKey.NAME),
            'Connection Template ID': this.blueprintId || null,
            'V2 Connections': isTrackingFieldEnabled(!!this.formDataCtrl.getValue(FieldKey.CONNECTION_VERSION)),
        });
    };

    private composeConnectionGroupsFromAPI = (
        connectionGroups: IConnectionTemplateConnectionGroupDTO[] = []
    ): IConnectionTemplateConnectionGroupDTO[] => {
        return connectionGroups.map(group => ({
            ...group,
            meetingConnectionId: uuid(),
            connectionGroupId: group.connectionGroupId || uuid(),
        }));
    };

    @action
    private updateRoleConnections(
        type: RoleConnectionTargetType,
        id: string,
        replacement: { type: RoleConnectionTargetType; id: string } | null,
    ): void {
        const next = { ...this.roleConnectionsState };
        (['presenter', 'host', 'support'] as RoleConnectionKey[]).forEach(role => {
            const assignment = next[role];

            if (assignment?.type === type && assignment.id === id) {
                next[role] = replacement;
            }
        });
        this.roleConnectionsState = next;
    }

    @action
    private clearRoleConnectionTarget(type: RoleConnectionTargetType, id: string): void {
        this.updateRoleConnections(type, id, null);
    }

    @action
    private retargetRoleConnection(type: RoleConnectionTargetType, previousId: string, nextId: string): void {
        this.updateRoleConnections(type, previousId, { type: type, id: nextId });
    }

    private roleTargetIsOnTemplate(assignment: { type: RoleConnectionTargetType; id: string }): boolean {
        if (assignment.type === 'connection') {
            return this.connections.some(connection =>
                connectionReferenceId(connection) === assignment.id || connection.connectionId === assignment.id
            );
        }
        return this.connectionGroups.some(group => group.connectionGroupId === assignment.id);
    }

    private toggleDefaultConnection = (
        entity: TConnection | IConnectionTemplateConnectionGroupDTO,
        isSelected: boolean
    ): void => {
        entity.isDefaultConnection = isSelected ? !entity.isDefaultConnection : false;
    };

    private buildPodiumFeaturesForMixpanel(): string {
        const isPodiumParticipantsEnabled = this.formDataCtrl.getValue(FieldKey.PODIUM_PARTICIPANTS_ENABLED);
        const isPodiumAttendeesEnabled = this.formDataCtrl.getValue(FieldKey.PODIUM_ATTENDEES_ENABLED);
        const isPodiumChatEnabled = this.formDataCtrl.getValue(FieldKey.PODIUM_CHAT_ENABLED);
        const isPodiumQuestionAnswerEnabled = this.formDataCtrl.getValue(FieldKey.PODIUM_QUESTION_ANSWER_ENABLED);

        return [
            isPodiumParticipantsEnabled && PODIUM_PARTICIPANTS_LABEL.defaultMessage,
            isPodiumAttendeesEnabled && PODIUM_ATTENDEES_LABEL.defaultMessage,
            isPodiumQuestionAnswerEnabled && PODIUM_QUESTION_ANSWER_LABEL.defaultMessage,
            isPodiumChatEnabled && PODIUM_CHAT_LABEL.defaultMessage,
        ].filter(Boolean).join(', ');
    }

    private updateConfigDefaultTrueValues = (initialConfigList: FormVmItemConfigType[], blueprintItemSnapshot: InitialData): void => {
        const snapshotKeys = new Set(Object.keys(blueprintItemSnapshot));
        this.updatedConfig = initialConfigList.map(c => {
            return {
                ...c,
                defaultValue: defaultTrueConfigValues.includes(c.name as FieldKey)
                    ? snapshotKeys.has(c.name)
                    : c.defaultValue
            };
        });
    };
}
